import React, { useEffect, useState } from "react";
import { Drawer, Pagination, ConfigProvider } from "antd";
import { FiSearch, FiFilter, FiX, FiMapPin, FiGift, FiCreditCard, FiRefreshCw, FiChevronDown } from "react-icons/fi";
import TallerCard from "../../Ui/TalleresCard/TallerCard";
import Talleres from "../../../service/Talleres";
import Recintos from "../../../service/Recintos";
import { useNotify } from "../../../providers/NotificationProvider";

const PRICE_OPTS = [
  { value: "todos",    label: "Todos",    icon: <FiRefreshCw size={14} /> },
  { value: "gratuitos",label: "Gratis",   icon: <FiGift size={14} /> },
  { value: "pago",     label: "De pago",  icon: <FiCreditCard size={14} /> },
];

const DEBOUNCE_MS = 350;

export default function TalleresDisponibles({ busqueda, onResumen }) {
  const notify = useNotify();
  const [talleres,     setTalleres]     = useState([]);
  const [recintos,     setRecintos]     = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [total,        setTotal]        = useState(0);
  const [stats,        setStats]        = useState({ gratuitos: 0, pago: 0 });
  const [drawerOpen,   setDrawerOpen]   = useState(false);
  const [recintosAlFinal, setRecintosAlFinal] = useState(false);

  // `search` es lo que hay escrito en el input; `busq` es lo que se consulta, con
  // debounce para no pedir una página por tecla.
  const [search,       setSearch]       = useState("");
  const [busq,         setBusq]         = useState("");
  const [recinto,      setRecinto]      = useState(null);
  const [tipoPrecio,   setTipoPrecio]   = useState("todos");
  const [page,         setPage]         = useState(1);
  const [pageSize,     setPageSize]     = useState(12);

  /* Búsqueda lanzada desde el hero: se aplica al tiro, sin esperar el debounce */
  useEffect(() => {
    if (!busqueda?.n) return;
    setSearch(busqueda.q); setBusq(busqueda.q); setPage(1);
  }, [busqueda]);

  useEffect(() => {
    if (search === busq) return;
    const t = setTimeout(() => { setBusq(search); setPage(1); }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [search, busq]);

  useEffect(() => {
    Recintos.List({ limit: 1000 })
      .then(res => setRecintos(res.data.sort((a, b) => a.nombre.localeCompare(b.nombre))))
      .catch(() => {});
  }, []);

  // Una sola consulta por cambio. Los filtros vuelven a la página 1 en su propio setter
  // (y no en un efecto aparte, que disparaba una segunda consulta). `vigente` descarta
  // respuestas que llegan tarde y pisarían a una más nueva.
  useEffect(() => {
    let vigente = true;
    setLoading(true);
    const params = { page, limit: pageSize };
    if (busq)                   params.search     = busq;
    if (recinto)                params.recinto    = recinto;
    if (tipoPrecio !== "todos") params.tipoPrecio = tipoPrecio;
    Talleres.List(params)
      .then(res => {
        if (!vigente) return;
        setTalleres(res.talleres || []);
        setTotal(res.total || 0);
        if (res.stats) setStats(res.stats);
      })
      .catch(() => {
        if (!vigente) return;
        notify.error("No se pudieron cargar los talleres");
        setTalleres([]); setTotal(0);
      })
      .finally(() => { if (vigente) setLoading(false); });
    return () => { vigente = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, busq, recinto, tipoPrecio]);

  const cambiarRecinto = (id) => { setRecinto(id); setPage(1); };
  const cambiarPrecio  = (v)  => { setTipoPrecio(v); setPage(1); };
  const limpiarBusqueda = () => { setSearch(""); setBusq(""); setPage(1); };
  const clearAll = () => { setSearch(""); setBusq(""); setRecinto(null); setTipoPrecio("todos"); setPage(1); };
  const activeFilters = [busq, recinto, tipoPrecio !== "todos"].filter(Boolean).length;
  const sinFiltros = activeFilters === 0;

  // Cifras para el hero: solo las de la consulta sin filtros, que es la oferta completa.
  useEffect(() => {
    if (loading || !sinFiltros) return;
    onResumen?.({ talleres: total, recintos: recintos.length, gratuitos: stats.gratuitos || 0 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, sinFiltros, total, recintos.length, stats.gratuitos]);

  const irAlListado = () => {
    document.getElementById("talleres-section")?.scrollIntoView({ behavior: "smooth" });
  };

  /* ── Panel de filtros (reutilizable en sidebar + drawer) ──
     Es una función que se llama, no un componente: como componente interno se
     desmontaba en cada render y la lista de recintos perdía su scroll. */
  const filtersBody = (enDrawer) => (
    <div className="space-y-6">
      {/* Tipo precio */}
      <div>
        <p className="text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">Tipo</p>
        <div className="flex flex-col gap-2">
          {PRICE_OPTS.map(o => (
            <button key={o.value} onClick={() => cambiarPrecio(o.value)} aria-pressed={tipoPrecio === o.value}
              className={`flex items-center gap-2.5 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all text-left
                ${tipoPrecio === o.value
                  ? "bg-primary text-white shadow-sm"
                  : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}>
              {o.icon} {o.label}
              {o.value === "todos"     && total > 0          && <span className="ml-auto text-[11px] opacity-70">{total}</span>}
              {o.value === "gratuitos" && stats.gratuitos > 0 && <span className="ml-auto text-[11px] opacity-70">{stats.gratuitos}</span>}
              {o.value === "pago"      && stats.pago > 0      && <span className="ml-auto text-[11px] opacity-70">{stats.pago}</span>}
            </button>
          ))}
        </div>
      </div>

      <div className="h-px bg-gray-100" />

      {/* Recinto */}
      <div>
        <p className="flex items-center text-[11px] font-bold uppercase tracking-widest text-gray-400 mb-3">
          <FiMapPin size={11} className="mr-1" />Recinto
          {recintos.length > 0 && (
            <span className="ml-auto px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] tracking-normal">{recintos.length}</span>
          )}
        </p>
        {/* La lista es más alta que su caja: barra fina + difuminado abajo para que se
            note que hay más recintos. El difuminado se va al llegar al final. */}
        <div className="relative">
        <div className="space-y-1 max-h-64 overflow-y-auto pr-1.5 scroll-fino"
          onScroll={e => {
            const el = e.currentTarget;
            setRecintosAlFinal(el.scrollTop + el.clientHeight >= el.scrollHeight - 4);
          }}>
          {recintos.map(r => (
            <button key={r._id}
              onClick={() => cambiarRecinto(recinto === r._id ? null : r._id)} aria-pressed={recinto === r._id}
              className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm transition-all text-left
                ${recinto === r._id ? "bg-primary text-white font-semibold" : "hover:bg-gray-50 text-gray-700"}`}>
              <span className="truncate">{r.nombre}</span>
              {recinto === r._id && <FiX size={13} />}
            </button>
          ))}
        </div>
        {recintos.length > 5 && !recintosAlFinal && (
          <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0 h-14 flex items-end justify-center pb-1
            bg-gradient-to-t from-white via-white/90 to-transparent">
            <span className="flex items-center gap-1 text-[11px] font-semibold text-gray-400">
              <FiChevronDown size={13} /> Desliza para ver más
            </span>
          </div>
        )}
        </div>
      </div>

      {/* Acciones. Los filtros se aplican solos; en el drawer solo falta cerrarlo. */}
      {(enDrawer || activeFilters > 0) && (
        <>
          <div className="h-px bg-gray-100" />
          <div className="flex gap-2">
            {activeFilters > 0 && (
              <button onClick={clearAll}
                className="flex-1 py-2.5 rounded-xl border border-gray-200 text-sm font-semibold text-gray-600 hover:bg-gray-50 transition-colors">
                Limpiar
              </button>
            )}
            {enDrawer && (
              <button onClick={() => setDrawerOpen(false)}
                className="flex-1 py-2.5 rounded-xl text-sm font-bold text-white bg-primary transition-colors">
                {loading ? "Buscando…" : `Ver ${total} taller${total !== 1 ? "es" : ""}`}
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  // Sin filtros y sin resultados: no hay oferta publicada, no es que la búsqueda falle.
  if (!loading && total === 0 && sinFiltros) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-24 h-24 rounded-full flex items-center justify-center text-5xl mb-6"
          style={{ background: '#e6eefb' }}>🏅</div>
        <h3 className="text-2xl font-bold text-gray-800 mb-2">Sin talleres disponibles</h3>
        <p className="text-gray-500 max-w-sm">Pronto habrá nuevas actividades. Vuelve a revisar en los próximos días.</p>
      </div>
    );
  }

  return (
    <div>
      {/* ── Header ── */}
      <div className="flex items-end justify-between mb-5">
        <div>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900">
            Talleres <span className="hl-amarillo">disponibles</span>
          </h2>
          <p className="text-sm text-gray-400 mt-0.5">
            {loading ? "Buscando…" : `${total} taller${total !== 1 ? "es" : ""} encontrado${total !== 1 ? "s" : ""}`}
          </p>
        </div>

        {/* Stats pills — desktop */}
        {!loading && total > 0 && (
          <div className="hidden sm:flex gap-2">
            {stats.gratuitos > 0 && (
              <span className="flex items-center gap-1.5 text-[12px] font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100">
                <FiGift size={13} /> {stats.gratuitos} gratis
              </span>
            )}
            {stats.pago > 0 && (
              <span className="flex items-center gap-1.5 text-[12px] font-semibold text-blue-700 bg-blue-50 px-3 py-1.5 rounded-full border border-blue-100">
                <FiCreditCard size={13} /> {stats.pago} de pago
              </span>
            )}
          </div>
        )}
      </div>

      {/* ── Search bar + filtros btn ── */}
      <div className="flex gap-2 mb-4">
        <div className="relative flex-1">
          <FiSearch size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
          <input
            type="search" value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar talleres…" aria-label="Buscar talleres"
            className="w-full pl-10 pr-10 py-3 bg-white rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:border-transparent shadow-sm"
            style={{ "--tw-ring-color": "#0047bb" }}
          />
          {search && (
            <button onClick={limpiarBusqueda} aria-label="Borrar búsqueda"
              className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
              <FiX size={15} />
            </button>
          )}
        </div>

        {/* Filtros btn (mobile + tablet) */}
        <button onClick={() => setDrawerOpen(true)}
          className="relative lg:hidden flex items-center gap-2 px-4 py-3 rounded-2xl border text-sm font-semibold shadow-sm transition-all"
          style={activeFilters > 0
            ? { background: "#0047bb", color: "#fff", borderColor: "#0047bb" }
            : { background: "#fff", color: "#374151", borderColor: "#e5e7eb" }
          }>
          <FiFilter size={16} />
          <span>Filtros</span>
          {activeFilters > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">
              {activeFilters}
            </span>
          )}
        </button>
      </div>

      {/* Active filter pills */}
      {activeFilters > 0 && (
        <div className="flex flex-wrap gap-2 mb-4">
          {busq && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium bg-gray-100 text-gray-700 px-3 py-1.5 rounded-full">
              "{busq}" <button onClick={limpiarBusqueda} aria-label="Quitar búsqueda"><FiX size={11} /></button>
            </span>
          )}
          {recinto && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
              <FiMapPin size={11} />{recintos.find(r => r._id === recinto)?.nombre}
              <button onClick={() => cambiarRecinto(null)} aria-label="Quitar recinto"><FiX size={11} /></button>
            </span>
          )}
          {tipoPrecio !== "todos" && (
            <span className="inline-flex items-center gap-1.5 text-[12px] font-medium bg-blue-50 text-blue-700 px-3 py-1.5 rounded-full">
              {tipoPrecio === "gratuitos" ? <><FiGift size={11} /> Gratis</> : <><FiCreditCard size={11} /> De pago</>}
              <button onClick={() => cambiarPrecio("todos")} aria-label="Quitar filtro de precio"><FiX size={11} /></button>
            </span>
          )}
          <button onClick={clearAll} className="text-[12px] text-gray-400 hover:text-gray-600 underline">
            Limpiar todo
          </button>
        </div>
      )}

      {/* ── Layout: sidebar + grid ── */}
      <div className="flex gap-6">
        {/* Sidebar desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div className="nd-card p-5 sticky top-24">
            <div className="flex items-center justify-between mb-4">
              <p className="font-bold text-gray-900 text-sm">Filtros</p>
              {activeFilters > 0 && (
                <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded-full"
                  style={{ background: "#0047bb" }}>
                  {activeFilters}
                </span>
              )}
            </div>
            {filtersBody(false)}
          </div>
        </aside>

        {/* Grid */}
        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="rounded-2xl bg-white overflow-hidden animate-pulse">
                  <div className="h-44 bg-gray-200" />
                  <div className="p-4 space-y-3">
                    <div className="h-4 bg-gray-200 rounded-full w-3/4" />
                    <div className="h-3 bg-gray-100 rounded-full w-1/2" />
                    <div className="h-3 bg-gray-100 rounded-full w-2/3" />
                    <div className="h-8 bg-gray-200 rounded-xl mt-2" />
                  </div>
                </div>
              ))}
            </div>
          ) : talleres.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {talleres.map(t => <TallerCard key={t._id} taller={t} />)}
              </div>

              {total > pageSize && (
                <div className="mt-8 flex justify-center">
                  <ConfigProvider theme={{ token: { colorPrimary: "#0047bb" } }}>
                    <Pagination
                      current={page} pageSize={pageSize} total={total}
                      onChange={(p, ps) => {
                        setPage(p);
                        if (ps !== pageSize) { setPageSize(ps); setPage(1); }
                        irAlListado();
                      }}
                      showSizeChanger
                      pageSizeOptions={["6", "12", "24", "48"]}
                      showTotal={(t, r) => `${r[0]}-${r[1]} de ${t}`}
                      responsive
                    />
                  </ConfigProvider>
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center py-24 gap-4 text-center">
              <div className="w-20 h-20 rounded-full flex items-center justify-center text-4xl"
                style={{ background: "#eff6ff" }}>🔍</div>
              <div>
                <p className="font-bold text-gray-800 text-lg mb-1">Sin resultados</p>
                <p className="text-sm text-gray-400 max-w-xs">
                  No encontramos talleres con esos filtros. Intenta con otros criterios.
                </p>
              </div>
              {activeFilters > 0 && (
                <button onClick={clearAll}
                  className="px-6 py-2.5 rounded-full text-sm font-bold text-white transition-colors"
                  style={{ background: "#0047bb" }}>
                  Limpiar filtros
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Drawer mobile */}
      <Drawer
        placement="bottom" height="80vh" open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        styles={{ body: { padding: 20 }, header: { padding: "16px 20px" } }}
        style={{ borderRadius: "20px 20px 0 0" }}
        title={
          <div className="flex items-center justify-between">
            <span className="font-bold text-gray-900">Filtros</span>
            {activeFilters > 0 && (
              <span className="text-[11px] font-bold text-white px-2 py-0.5 rounded-full"
                style={{ background: "#0047bb" }}>{activeFilters}</span>
            )}
          </div>
        }
      >
        {filtersBody(true)}
      </Drawer>
    </div>
  );
}
